import { IconDownload, IconPlus, IconQrCode, IconUpload } from '@myplayer/ui';
import { createContext, useContext, useEffect, useRef, useState, type ReactElement } from 'react';
import { Button } from './components/ui/primitives.js';
import { openQrCheckInNewTab } from './qr-check-helper.js';
import { ADMIN_STRINGS } from './strings.js';

export const InsideAdminShellContext = createContext<boolean>(false);

export function useInsideAdminShell(): boolean {
  return useContext(InsideAdminShellContext);
}

export type RailActionPageContext =
  'overview' | 'products' | 'domains' | 'audit-log' | 'settings' | 'help';

export interface RailActionMenuProps {
  readonly pageContext?: RailActionPageContext;
  readonly canCreateIdentifier: boolean;
  readonly canExportIdentifier: boolean;
  readonly canManageDomains?: boolean;
  readonly canExportAuditLog?: boolean;
  readonly onAddProduct: () => void;
  readonly onImportCsv: () => void;
  readonly onExportCsv: () => void;
  readonly onCheckPrintedQr?: () => void;
  readonly onAddDomain?: () => void;
  readonly onExportAuditCsv?: () => void;
}

export function resolveRailActionPageContext(pathname: string): RailActionPageContext {
  if (pathname.startsWith('/products')) return 'products';
  if (pathname.startsWith('/domains')) return 'domains';
  if (pathname.startsWith('/audit-log') || pathname.startsWith('/activity')) return 'audit-log';
  if (pathname.startsWith('/settings') || pathname.startsWith('/brand')) return 'settings';
  if (pathname.startsWith('/help')) return 'help';
  return 'overview';
}

export function RailActionMenu({
  pageContext = 'products',
  canCreateIdentifier,
  canExportIdentifier,
  canManageDomains = false,
  canExportAuditLog = false,
  onAddProduct,
  onImportCsv,
  onExportCsv,
  onCheckPrintedQr,
  onAddDomain,
  onExportAuditCsv,
}: RailActionMenuProps): ReactElement | null {
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMenuOpen(false);
  }, [pageContext]);

  useEffect(() => {
    if (!menuOpen || typeof document === 'undefined') return;
    const handlePointerDown = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  if (pageContext === 'overview' || pageContext === 'settings' || pageContext === 'help') {
    return null;
  }

  if (pageContext === 'domains') {
    if (!canManageDomains) return null;
    return (
      <div
        ref={containerRef}
        data-testid="rail-action-menu"
        className="group fixed bottom-[76px] right-4 z-40 flex shrink-0 items-center justify-center md:relative md:bottom-auto md:right-auto md:h-[52px] md:w-[68px]"
      >
        <Button
          type="button"
          variant="primary"
          size="rail"
          data-testid="rail-action-trigger"
          aria-label="Add Domain"
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          title="Add Domain"
          onClick={() => setMenuOpen((prev) => !prev)}
          className={`shrink-0 transition-transform hover:-translate-y-0.5 ${
            menuOpen ? 'rotate-45' : ''
          }`}
        >
          <IconPlus />
        </Button>

        <div
          role="menu"
          aria-label="Domain actions"
          data-testid="rail-subactions-menu"
          className={`absolute bottom-[60px] right-0 z-50 flex min-w-[172px] flex-col gap-1.5 rounded-card border border-border bg-surface p-1.5 shadow-card-base transition-all duration-150 md:bottom-auto md:left-[68px] md:right-auto md:top-0 ${
            menuOpen
              ? 'pointer-events-auto translate-y-0 opacity-100 md:translate-x-0'
              : 'pointer-events-none translate-y-1 opacity-0 group-focus-within:pointer-events-auto group-focus-within:translate-y-0 group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 md:-translate-x-1 md:translate-y-0 md:group-focus-within:translate-x-0 md:group-hover:translate-x-0'
          }`}
        >
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="domains-rail-add-btn"
            onClick={() => {
              setMenuOpen(false);
              onAddDomain?.();
            }}
            className="w-full justify-start gap-2.5 px-3 py-2 text-[13px] font-semibold text-fg hover:bg-brand-wash hover:text-brand"
          >
            <IconPlus className="h-4 w-4 text-brand" />
            <span>Add domain</span>
          </Button>
        </div>
      </div>
    );
  }

  if (pageContext === 'audit-log') {
    if (!canExportAuditLog) return null;
    return (
      <div
        ref={containerRef}
        data-testid="rail-action-menu"
        className="group fixed bottom-[76px] right-4 z-40 flex shrink-0 items-center justify-center md:relative md:bottom-auto md:right-auto md:h-[52px] md:w-[68px]"
      >
        <Button
          type="button"
          variant="primary"
          size="rail"
          data-testid="rail-action-trigger"
          aria-label="Audit log actions"
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          title="Export Audit CSV"
          onClick={() => setMenuOpen((prev) => !prev)}
          className="shrink-0 transition-transform hover:-translate-y-0.5"
        >
          <IconDownload />
        </Button>

        <div
          role="menu"
          aria-label="Audit log actions"
          data-testid="rail-subactions-menu"
          className={`absolute bottom-[60px] right-0 z-50 flex min-w-[172px] flex-col gap-1.5 rounded-card border border-border bg-surface p-1.5 shadow-card-base transition-all duration-150 md:bottom-auto md:left-[68px] md:right-auto md:top-0 ${
            menuOpen
              ? 'pointer-events-auto translate-y-0 opacity-100 md:translate-x-0'
              : 'pointer-events-none translate-y-1 opacity-0 group-focus-within:pointer-events-auto group-focus-within:translate-y-0 group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 md:-translate-x-1 md:translate-y-0 md:group-focus-within:translate-x-0 md:group-hover:translate-x-0'
          }`}
        >
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="audit-rail-export-csv-btn"
            onClick={() => {
              setMenuOpen(false);
              onExportAuditCsv?.();
            }}
            className="w-full justify-start gap-2.5 px-3 py-2 text-[13px] font-medium text-fg hover:bg-brand-wash hover:text-brand"
          >
            <IconDownload className="h-4 w-4 text-fg-secondary" />
            <span>Export CSV</span>
          </Button>
        </div>
      </div>
    );
  }

  if (!canCreateIdentifier && !canExportIdentifier) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      data-testid="rail-action-menu"
      className="group fixed bottom-[76px] right-4 z-40 flex shrink-0 items-center justify-center md:relative md:bottom-auto md:right-auto md:h-[52px] md:w-[68px]"
    >
      {canCreateIdentifier && (
        <Button
          type="button"
          variant="primary"
          size="rail"
          data-testid="rail-action-trigger"
          aria-label={ADMIN_STRINGS.navigation.newProductButton}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          title={ADMIN_STRINGS.navigation.newProductButton}
          onClick={() => setMenuOpen((prev) => !prev)}
          className={`shrink-0 transition-transform hover:-translate-y-0.5 ${
            menuOpen ? 'rotate-45' : ''
          }`}
        >
          <IconPlus />
        </Button>
      )}
      {!canCreateIdentifier && canExportIdentifier && (
        <Button
          type="button"
          variant="primary"
          size="rail"
          data-testid="rail-action-trigger"
          aria-label="Product actions"
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          title="Product actions"
          onClick={() => setMenuOpen((prev) => !prev)}
          className="shrink-0 transition-transform hover:-translate-y-0.5"
        >
          <IconDownload />
        </Button>
      )}

      <div
        role="menu"
        aria-label="Product actions"
        data-testid="rail-subactions-menu"
        className={`absolute bottom-[60px] right-0 z-50 flex min-w-[184px] flex-col gap-1.5 rounded-card border border-border bg-surface p-1.5 shadow-card-base transition-all duration-150 md:bottom-auto md:left-[68px] md:right-auto md:top-0 ${
          menuOpen
            ? 'pointer-events-auto translate-y-0 opacity-100 md:translate-x-0'
            : 'pointer-events-none translate-y-1 opacity-0 group-focus-within:pointer-events-auto group-focus-within:translate-y-0 group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 md:-translate-x-1 md:translate-y-0 md:group-focus-within:translate-x-0 md:group-hover:translate-x-0'
        }`}
      >
        {canCreateIdentifier && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="products-add-product-btn"
            onClick={() => {
              setMenuOpen(false);
              onAddProduct();
            }}
            className="w-full justify-start gap-2.5 px-3 py-2 text-[13px] font-semibold text-fg hover:bg-brand-wash hover:text-brand"
          >
            <IconPlus className="h-4 w-4 text-brand" />
            <span>Add product</span>
          </Button>
        )}
        {canCreateIdentifier && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="toggle-import-csv-btn"
            onClick={() => {
              setMenuOpen(false);
              onImportCsv();
            }}
            className="w-full justify-start gap-2.5 px-3 py-2 text-[13px] font-medium text-fg hover:bg-brand-wash hover:text-brand"
          >
            <IconUpload className="h-4 w-4 text-fg-secondary" />
            <span>Import CSV</span>
          </Button>
        )}
        {canExportIdentifier && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="export-csv-btn"
            onClick={() => {
              setMenuOpen(false);
              onExportCsv();
            }}
            className="w-full justify-start gap-2.5 px-3 py-2 text-[13px] font-medium text-fg hover:bg-brand-wash hover:text-brand"
          >
            <IconDownload className="h-4 w-4 text-fg-secondary" />
            <span>Export CSV</span>
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid="products-open-qr-check-btn"
          onClick={() => {
            setMenuOpen(false);
            if (onCheckPrintedQr) {
              onCheckPrintedQr();
            } else {
              openQrCheckInNewTab();
            }
          }}
          className="w-full justify-start gap-2.5 px-3 py-2 text-[13px] font-medium text-fg hover:bg-brand-wash hover:text-brand"
        >
          <IconQrCode className="h-4 w-4 text-fg-secondary" />
          <span>Check printed QR</span>
        </Button>
      </div>
    </div>
  );
}
